import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Table,
  Row,
  Col,
  Badge,
  Spinner,
  Container,
  Card,
} from "react-bootstrap";
import Swal from "sweetalert2";
import axios from "axios";
import { BASE_URL } from "../../services/Api";
import { authHeader } from "../../services/auth-header";

const BomPumpViewPage = () => {
  const { pointindex, id } = useParams();
  const navigate = useNavigate();
  const [bomData, setBomData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchBomData = async () => {
      try {
        const response = await axios.get(`${BASE_URL}bom/${id}`, {
          headers: authHeader(),
        });
        setBomData(response.data);
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch Pump BOM data.",
        });
        navigate(-1);
      } finally {
        setIsLoading(false);
      }
    };

    fetchBomData();
  }, [id, navigate]);

  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(value || 0);

  if (isLoading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!bomData) {
    return <div className="alert alert-danger mt-3">Pump BOM data not found</div>;
  }


  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.delete(`${BASE_URL}bom/${id}`, {
          headers: authHeader(),
        });
        navigate(`/bomindex/${pointindex}`);
        Swal.fire('Deleted!', 'BOM deleted successfully.', 'success');
      } catch (error) {
        Swal.fire('Error', error.response?.data || 'An error occurred', 'error');
      }
    }
  };

  return (
    <Card className="shadow-lg p-4 rounded-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="fw-bold text-primary">Type: {bomData.bomtype}</h5>
        <div className="d-flex gap-2">
          <button className="btn btn-danger" onClick={() => handleDelete(id)}>Delete</button>
          <Link to={`/BOMEdit/${id}/${pointindex}`} className="btn btn-warning">Edit</Link>
          <Link to={`/bomindex/${pointindex}`} className="btn btn-secondary">Back</Link>
        </div>
      </div>
      <Container>
        <div className="mb-4 text-center">
          <h4 className="fw-bold">Pump BOM - View</h4>
        </div>

        {/* BOM Header */}
        <div className="mb-4 border p-3">
          <Row>
            <Col md={4}><strong>BOM ID:</strong> {bomData.id || "N/A"}</Col>
            <Col md={4}><strong>Product Name (Finished):</strong> {bomData.finalfinishedproduct?.description || "N/A"}</Col>
            <Col md={4}><strong>Part No.:</strong> {bomData.finalfinishedproduct?.partno || "N/A"}</Col>
            <Col md={4}><strong>Job No.:</strong> {bomData.jobnumber || "N/A"}</Col>
            <Col md={4}>
              <strong>Status:</strong>{" "}
              <Badge bg={bomData.status === "Open" ? "success" : "secondary"}>
                {bomData.status || "Close"}
              </Badge>
            </Col>
          </Row>
        </div>

        {/* Raw/Semi/Service Items Table */}
        <h5 className="text-primary">Raw/Semi/Service Products</h5>
        <div className="table-responsive mb-4">
          <Table bordered size="sm">
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Description</th>
                <th>Part No.</th>
                <th>MOC</th>
                <th>Size</th>
                <th>Quantity</th>
                <th>unit of measurement</th>
                <th>Rate</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {bomData.bomPumpPartsItemsRawSemiService?.map((item, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{item.description}</td>
                  <td>{item.partno}</td>
                  <td>{item.moc}</td>
                  <td>{item.size}</td>
                  <td>{item.pumprequiredquantity}</td>
                  <td>{item.uom}</td>
                  <td>{formatCurrency(item.rate)}</td>
                  <td>{formatCurrency(item.amount)}</td>
                </tr>
              ))}
              <tr className="fw-bold">
                <td colSpan="7" className="text-end">Total:</td>
                <td>{formatCurrency(bomData.pumpbomrawmaterialtotalcost)}</td>
              </tr>
            </tbody>
          </Table>
        </div>

        {/* Summary Section */}
        <Row className="border p-3 mb-4">
          <Col md={4}><strong>Net Amount:</strong> {formatCurrency(bomData.pumpbomrawmaterialtotalcost)}</Col>
          <Col md={4}><strong>Labour Charges:</strong> {formatCurrency(bomData.labourcharges)}</Col>
          <Col md={4}><strong>Grand Total:</strong> {formatCurrency(bomData.grandtotalpump)}</Col>
        </Row>

        {/* Created / Updated Info */}
        <Row className="border p-3 mb-4">
          <Col md={4}><strong>Created By:</strong> {bomData.createbyname || "N/A"}</Col>
          <Col md={4}><strong>Created Date:</strong> {bomData.createddate || "N/A"}</Col>
          <Col md={4}><strong>Created Time:</strong> {bomData.createdtime || "N/A"}</Col>
          <Col md={4} className="mt-3"><strong>Updated By:</strong> {bomData.updatedbyname || "N/A"}</Col>
          <Col md={4} className="mt-3"><strong>Updated Date:</strong> {bomData.updateddate || "N/A"}</Col>
          <Col md={4} className="mt-3"><strong>Updated Time:</strong> {bomData.updatedtime || "N/A"}</Col>
        </Row>
      </Container>
    </Card>
  );
};

export default BomPumpViewPage;